import { Link } from "react-router-dom";
import { ArrowRight, Zap, ClipboardCheck, Users, Store, GraduationCap, BarChart3, ShieldCheck, FileText, Calculator, LineChart } from "lucide-react";
import { useI18n } from "../i18n";
import SEO from "../components/SEO";

const MODULES = [
  { icon: Calculator, key: "valuation", route: "/valutazione", it: { title: "Motore di valutazione istantanea", body: "Bilancio caricato → EBITDA normalizzato, PFN reale, range di valore in tre scenari. In 10 minuti. Gratis." } },
  { icon: LineChart, key: "dscr", route: "/simulatore-sostenibilita", it: { title: "Simulatore di sostenibilità", body: "Cassetta zero-equity (vendor loan, earn-out, affitto-riscatto, garanzie). Se i flussi non reggono, il deal non parte." } },
  { icon: Store, key: "marketplace", route: "/marketplace", it: { title: "Marketplace a rivelazione progressiva", body: "Profili verificati. Prova fondi obbligatoria. Anonimato del venditore fino all'NDA digitale. Zero perditempo." } },
  { icon: Users, key: "partner", route: "/partner", it: { title: "Rete Partner Pro", body: "Kit pre-qualifica per commercialisti e advisor. Dal bilancio fiscale al dossier vendibile in 48 ore. Success fee trasparente." } },
  { icon: GraduationCap, key: "academy", route: "/academy", it: { title: "Academy dei Direttori Generali", body: "Il collo di bottiglia dei serial acquirer è chi guida l'azienda dopo il fondatore. Formiamo manager 35-45 anni con equity virtuale." } },
  { icon: BarChart3, key: "osservatorio", route: "/osservatorio", it: { title: "Osservatorio successioni PMI", body: "Indice trimestrale con multipli reali del micro-market italiano. Dati che oggi non esistono." } },
  { icon: ShieldCheck, key: "registro", route: "/registro-promesse", it: { title: "Registro delle Promesse", body: "Dashboard aperta: per ogni azienda tipo, dipendenti, sede, marchio, investimenti. La trasparenza come vantaggio competitivo." } },
  { icon: FileText, key: "standard", route: "/standard-agreement", it: { title: "GIANO Standard Agreement", body: "Il SAFE di Y Combinator per le PMI italiane. Contratto di cessione standard, pubblico, gratuito. Chi lo usa parla la lingua di GIANO." } },
];

const PILLARS = [
  { n: "01", it: { title: "La macchina di acquisizione più veloce d'Italia", body: "Offerta in 15 giorni, chiusura in 90-120. Contro i 12-15 mesi di mercato. Accelerazione ~80%." } },
  { n: "02", it: { title: "La piattaforma dove domanda e offerta si incontrano", body: "Marketplace a tre lati: azienda, compratore, capitale. Ogni utente esterno alimenta la pipeline." } },
  { n: "03", it: { title: "Un business che si finanzia da solo", body: "Ricavi ricorrenti da abbonamenti che pagano l'infrastruttura e generano il radar di origination più potente d'Italia." } },
];

export default function Piattaforma() {
  const { lang } = useI18n();
  const t = lang === "it";
  return (
    <main data-testid="piattaforma-page">
      <SEO
        title="La Piattaforma"
        description="La piattaforma tecnologica di GIANO: motore di valutazione istantanea, simulatore di sostenibilità, marketplace verificato, rete Partner Pro, Academy DG, Osservatorio successioni PMI, Registro delle Promesse e Standard Agreement open-source."
        path="/piattaforma"
      />
      {/* HERO */}
      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-24 md:py-32">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>La Piattaforma</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 1.05, maxWidth: 1100 }}>
            {t ? (
              <>
                Un solo motore.<br />
                <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Due facce.</span>{" "}
                Un'infrastruttura per il mercato PMI italiano.
              </>
            ) : (
              <>
                One engine.<br />
                <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Two sides.</span>{" "}
                An infrastructure for the Italian SME market.
              </>
            )}
          </h1>
          <p className="mt-10 text-lg md:text-xl" style={{ opacity: 0.82, maxWidth: 780, lineHeight: 1.7 }}>
            {t
              ? "GIANO è due cose insieme: una holding permanente che acquisisce PMI italiane senza successione, e la piattaforma tecnologica che rende il deal veloce, prevedibile e comprensibile a tutti gli stakeholder — imprenditori, commercialisti, banche, investitori."
              : "GIANO is two things at once: a permanent holding that acquires Italian SMEs without succession, and the technology platform that makes the deal fast, predictable, and understandable to every stakeholder."}
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PILLARS.map((p) => (
              <div key={p.n} style={{ borderTop: "1px solid rgba(247,245,240,0.2)", paddingTop: 24 }}>
                <div className="font-mono text-xs" style={{ color: "var(--gold)" }}>{p.n}</div>
                <h3 className="serif-display mt-3" style={{ fontSize: 22, lineHeight: 1.25 }}>{p.it.title}</h3>
                <p className="mt-3 text-sm" style={{ opacity: 0.78, lineHeight: 1.65 }}>{p.it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES GRID */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <div className="mb-16">
            <div className="overline" style={{ color: "var(--terracotta)" }}>{t ? "8 moduli" : "8 modules"}</div>
            <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 60px)" }}>
              {t ? "Il volano completo." : "The complete flywheel."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)" }}>
            {MODULES.map((m, idx) => (
              <Link
                key={m.key}
                to={m.route}
                data-testid={`module-${m.key}`}
                className="p-8 md:p-10 group"
                style={{
                  borderRight: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)",
                  background: idx % 4 === 0 ? "var(--muted)" : "var(--card)",
                  color: "inherit",
                  textDecoration: "none",
                  transition: "background 0.3s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bone)"}
                onMouseLeave={e => e.currentTarget.style.background = idx % 4 === 0 ? "var(--muted)" : "var(--card)"}
              >
                <div className="flex items-center justify-between mb-6">
                  <m.icon size={22} style={{ color: "var(--terracotta)" }} />
                  <span className="font-mono text-xs" style={{ opacity: 0.5 }}>0{idx + 1}</span>
                </div>
                <h3 className="serif-display" style={{ fontSize: 26, lineHeight: 1.2 }}>{m.it.title}</h3>
                <p className="mt-4 text-sm" style={{ opacity: 0.75, lineHeight: 1.65 }}>{m.it.body}</p>
                <div className="mt-8 inline-flex items-center gap-2 font-mono text-xs group-hover:gap-3 transition-all">
                  {t ? "Scopri" : "Explore"} <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="py-24 md:py-32" style={{ background: "var(--muted)" }}>
        <div className="container-x text-center">
          <div className="overline" style={{ color: "var(--terracotta)" }}>{t ? "Abbonamenti" : "Subscriptions"}</div>
          <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 60px)", maxWidth: 900, margin: "0 auto" }}>
            {t
              ? "Monitoraggio continuo, non fotografie costose."
              : "Continuous monitoring, not expensive snapshots."}
          </h2>
          <p className="mt-8 text-lg" style={{ opacity: 0.78, maxWidth: 720, margin: "0 auto", lineHeight: 1.7 }}>
            {t
              ? "Da 29€ al mese per l'imprenditore che vuole sapere quanto vale la sua azienda, fino alle licenze enterprise per banche e confidi."
              : "From €29/month for entrepreneurs who want to know what their company is worth, up to enterprise licences for banks and credit institutions."}
          </p>
          <Link to="/prezzi" data-testid="pricing-cta" className="btn-primary mt-10 inline-flex">
            {t ? "Vedi i piani" : "See the plans"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
